import React from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "@/components/Seo";
import { Breadcrumbs } from "@/components/layout/Breadcrumbs";
import { TestDriveForm } from "@/components/forms/TestDriveForm";
import { MapView } from "@/components/MapView";
import NotFound from "@/pages/public/NotFound";
import { MapPin, Phone, Clock, Navigation, CheckCircle2, Accessibility, ParkingSquare, Star } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";

export default function LocationPage() {
  const { city } = useParams();
  const { data: l, isLoading, isError } = useQuery({
    queryKey: ["location", city], queryFn: () => api.get(`/locations/${city}`).then((r) => r.data), retry: false,
  });
  if (isLoading) return <div className="max-w-4xl mx-auto px-4 py-20 text-slate-500">Caricamento...</div>;
  if (isError || !l) return <NotFound />;

  const business = l.indexable ? {
    "@context": "https://schema.org", "@type": "LocalBusiness", name: `${l.dealer_name} — Ordina SENIORCAR a ${l.city}`,
    description: l.local_description,
    address: { "@type": "PostalAddress", streetAddress: l.address, addressLocality: l.city, addressRegion: l.province, addressCountry: "IT" },
    telephone: l.telephone, areaServed: l.areas_served,
    geo: { "@type": "GeoCoordinates", latitude: l.latitude, longitude: l.longitude },
  } : null;
  const faqLd = (l.indexable && l.faq?.length) ? {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: l.faq.map(([q, a]) => ({ "@type": "Question", name: q, acceptedAnswer: { "@type": "Answer", text: a } })),
  } : null;
  const jsonLd = business ? (faqLd ? [business, faqLd] : business) : null;

  return (
    <>
      <Seo title={`Ordina SENIORCAR a ${l.city}`}
        description={l.local_description ? l.local_description.slice(0, 158) : `Ordina online SENIORCAR a ${l.city} con il supporto del centro più vicino.`}
        keywords={`ordina seniorcar ${l.city.toLowerCase()}, dispositivo medico mobilità ${l.city.toLowerCase()}`}
        noindex={!l.indexable} jsonLd={jsonLd} />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
        <Breadcrumbs items={[{ label: "Ordina online", path: "/prova-seniorcar" }, { label: l.city }]} />
      </div>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 pt-6">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight" style={{ fontFamily: "Outfit" }}>Ordina SENIORCAR a {l.city}</h1>
        {l.local_description && <p className="mt-4 text-lg sm:text-xl text-slate-600 leading-relaxed">{l.local_description}</p>}

        {l.latitude ? <div className="mt-8"><MapView points={[{ id: l.slug, lat: l.latitude, lng: l.longitude, title: l.dealer_name, subtitle: l.address }]} height={320} /></div> : null}

        <div className="mt-8 grid sm:grid-cols-2 gap-4">
          <div className="rounded-2xl bg-white border border-slate-200 p-5 space-y-3 text-slate-700">
            <h3 className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>{l.dealer_name}</h3>
            {l.address && <p className="flex items-start gap-2 text-sm"><MapPin size={18} className="mt-0.5 text-emerald-700 shrink-0" /> {l.address}</p>}
            {l.telephone && <p className="flex items-center gap-2 text-sm"><Phone size={18} className="text-emerald-700" /> <a href={`tel:${l.telephone}`} className="hover:text-emerald-800">{l.telephone}</a></p>}
            {l.opening_hours && <p className="flex items-start gap-2 text-sm"><Clock size={18} className="mt-0.5 text-emerald-700 shrink-0" /> {l.opening_hours}</p>}
            {l.test_drive_days && <p className="flex items-start gap-2 text-sm"><CheckCircle2 size={18} className="mt-0.5 text-emerald-700 shrink-0" /> Giorni disponibili: {l.test_drive_days}</p>}
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-5 space-y-3 text-slate-700 text-sm">
            {l.accessibility && <p className="flex items-start gap-2"><Accessibility size={18} className="mt-0.5 text-emerald-700 shrink-0" /> {l.accessibility}</p>}
            {l.directions && <p className="flex items-start gap-2"><Navigation size={18} className="mt-0.5 text-emerald-700 shrink-0" /> {l.directions}</p>}
            {l.parking && <p className="flex items-start gap-2"><ParkingSquare size={18} className="mt-0.5 text-emerald-700 shrink-0" /> {l.parking}</p>}
            {l.areas_served?.length > 0 && <p className="text-slate-500">Zone servite: {l.areas_served.join(", ")}</p>}
          </div>
        </div>

        {l.reviews?.length > 0 && (
          <div className="mt-8">
            <h2 className="text-xl font-bold text-slate-900 mb-3" style={{ fontFamily: "Outfit" }}>Cosa dicono i clienti</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {l.reviews.map((rv, i) => (
                <div key={i} className="rounded-2xl bg-white border border-slate-200 p-5">
                  <div className="flex gap-1 mb-2">{[...Array(5)].map((_, k) => <Star key={k} size={16} className="text-amber-500 fill-amber-500" />)}</div>
                  <p className="text-slate-600 italic">"{rv.text}"</p>
                  <p className="text-slate-500 text-sm mt-2 font-semibold">— {rv.author}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      <section className="max-w-4xl mx-auto px-4 sm:px-6 mt-12">
        <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit" }}>Ordina online a {l.city}</h2>
        <p className="text-slate-600 mb-6">Compila il modulo: ti contatteremo per confermare giorno e orario.</p>
        <TestDriveForm />
      </section>

      {l.faq?.length > 0 && (
        <section className="max-w-4xl mx-auto px-4 sm:px-6 mt-12">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: "Outfit" }}>Domande frequenti — {l.city}</h2>
          <div className="sc-prose">{l.faq.map(([q, a], i) => <div key={i}><h3>{q}</h3><p>{a}</p></div>)}</div>
        </section>
      )}
      <div className="h-16" />
    </>
  );
}
